import dayjs, { Dayjs } from 'dayjs';
import { Button, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { ruRU } from '@mui/x-date-pickers/locales';

import { DATE_PICKER_SLOT_PROPS, detectPreset, PRESETS } from '../../time-interval-utils';

export interface TimeFilterState {
    from: Dayjs | null;
    to: Dayjs | null;
}

export function detectPresetNullable(from: Dayjs | null, to: Dayjs | null): number | null {
    if (!from || !to) return null;
    return detectPreset(from, to);
}

const FROM_SLOT_PROPS = {
    ...DATE_PICKER_SLOT_PROPS,
    textField: {
        size: 'small' as const,
        sx: {
            width: 190,
            '& .MuiInputBase-input': { py: 0.5, px: 1 },
        },
    },
    field: { clearable: true },
};

const TO_SLOT_PROPS = {
    ...FROM_SLOT_PROPS,
    actionBar: { actions: ['today' as const] },
};

//=================================================================================================
interface Props {
    state: TimeFilterState;
    onChange: (state: TimeFilterState) => void;
}

export default function TimeFilter({ state: { from, to }, onChange }: Props) {
    const activePreset = detectPresetNullable(from, to);

    const applyPreset = (idx: number) => {
        const base = (to ?? dayjs()).endOf('day');
        const days = PRESETS[idx].days - 1;
        onChange({ from: base.subtract(days, 'day').startOf('day'), to: base });
    };

    const handleFromChange = (value: Dayjs | null) => {
        onChange({ from: value ? value.startOf('day') : null, to });
    };

    const handleToChange = (value: Dayjs | null) => {
        onChange({ from, to: value ? value.endOf('day') : null });
    };

    return (
        <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="ru"
            localeText={ruRU.components.MuiLocalizationProvider.defaultProps.localeText}
        >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ m: 1, flexWrap: 'wrap', gap: 1 }}>
                <Stack
                    direction="row"
                    sx={{
                        border: 0.666,
                        borderColor: 'divider',
                        borderRadius: 1,
                        height: 32,
                        px: 0.5,
                        gap: 0.5,
                        alignItems: 'center',
                        transition: 'border-color 0.2s',
                        '&:hover': { borderColor: 'text.primary' },
                        '&:focus-within': { borderColor: 'primary.main' },
                    }}
                >
                    {PRESETS.map((preset, idx) => (
                        <Button
                            key={preset.label}
                            variant={activePreset === idx ? 'contained' : 'text'}
                            onClick={() => applyPreset(idx)}
                            sx={{
                                minWidth: 0,
                                px: 1,
                                py: 0.1,
                                textTransform: 'none',
                                fontWeight: 400,
                                lineHeight: 1.5,
                            }}
                        >
                            {preset.label}
                        </Button>
                    ))}
                </Stack>

                <Typography variant="body2" color="text.primary">
                    от:
                </Typography>
                <DatePicker
                    value={from}
                    onChange={handleFromChange}
                    maxDate={to ?? undefined}
                    format="DD.MM.YYYY"
                    slotProps={FROM_SLOT_PROPS}
                />

                <Typography variant="body2" color="text.primary">
                    до:
                </Typography>
                <DatePicker
                    value={to}
                    onChange={handleToChange}
                    minDate={from ?? undefined}
                    format="DD.MM.YYYY"
                    slotProps={TO_SLOT_PROPS}
                />
            </Stack>
        </LocalizationProvider>
    );
}
